import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import Geohash from "latlon-geohash";
import { httpGet } from "@/services/api.service";

import {
  GRIDS_TYPES,
  GRIDS_STABILITY_TYPES,
  GRIDS_SCORE_RANGE_DEFAULTS,
} from "@/common/constants/grids";
import {
  getCountryByCode,
  getCityByCode,
  getGeohashByCode,
} from "@/common/maps/utilities";
import { ENDPOINT_AOIS } from "@/common/constants/api";

export { _getVarianceScoreColorStyle as getVarianceScoreColorStyle };
export { _getVarianceScoreRangeDefaults as getVarianceScoreRangeDefaults };
export { _setGridLevelFilters as setGridLevelFilters };
export { _getVarianceScoreRanges as getVarianceScoreRanges };
export { _getVarianceScoreColor as getVarianceScoreColor };
//TODO remove getChartLineColors if it isn't used anywhere
export { _getChartLineColors as getChartLineColors };
export { _getLocationByType as getLocationByType };
export { _getCityObjectNearGeohash as getCityObjectNearGeohash };
export { _getCityNearGeohash as getCityNearGeohash };
export { _getColorByStabilityScore as getColorByStabilityScore };

const _getVarianceScoreColorStyle = (options) => {
  const VSR_DEFAULTS = _getVarianceScoreRangeDefaults(options.type);

  let result = ["step"];

  if (options.field_expression) {
    result.push(options.field_expression);
  } else {
    options.field = options.field ? options.field : "score";
    result.push(["get", options.field]);
  }

  for (let i = 0; i < VSR_DEFAULTS.length; i++) {
    if (i !== 0) {
      result.push(VSR_DEFAULTS[i].min);
    }

    let hasColor = true;
    if (
      options.filters &&
      Array.isArray(options.filters) &&
      options.filters[i]
    ) {
      hasColor = options.filters[i].selected;
    }

    if (hasColor) {
      result.push(VSR_DEFAULTS[i].color);
    } else {
      result.push("rgba(255, 255, 255, 0.0001)");
    }
  }
  return result;
};

const _getVarianceScoreRangeDefaults = (type) => {
  if (type === GRIDS_TYPES.activity) {
    return JSON.parse(JSON.stringify(GRIDS_SCORE_RANGE_DEFAULTS.activity));
  } else {
    return JSON.parse(JSON.stringify(GRIDS_SCORE_RANGE_DEFAULTS.stability));
  }
};

const _setGridLevelFilters = (params) => {
  const VARIANCE_SCORE_RANGES = _getVarianceScoreRanges({ type: params.type });

  let ranges = [];
  for (let i = 0; i < VARIANCE_SCORE_RANGES.length; i++) {
    let range = lodashCloneDeep(VARIANCE_SCORE_RANGES[i]);
    for (let j = 0; j < params.levels.length; j++) {
      const LEVEL = params.levels[j];
      if (range.id === LEVEL.id) {
        range.selected = LEVEL.selected;
        ranges.push(range);
        break;
      }
    }
  }
  return ranges;
};

const _getVarianceScoreRanges = (params) => {
  var ranges = [];
  var varianceScoreRangeDefaults = _getVarianceScoreRangeDefaults(params.type);
  for (var i = 0; i < varianceScoreRangeDefaults.length; i++) {
    var vsr = varianceScoreRangeDefaults[i];
    var newVsr = {
      id: vsr.id,
      min: vsr.min,
      max: vsr.max,
      selected: vsr.selected,
    };
    ranges.push(newVsr);
  }
  return ranges;
};

const _getVarianceScoreColor = (params) => {
  let color = "";
  const VSR_DEFAULTS = _getVarianceScoreRangeDefaults(params.type);
  for (var i = 0; i < VSR_DEFAULTS.length; i++) {
    const VSR = VSR_DEFAULTS[i];
    const IS_EVEN = VSR.id % 2 === 0; // if is one of the even ranges then the values are inclusive (>= and <=)
    if (IS_EVEN) {
      if (params.varianceScore >= VSR.min && params.varianceScore <= VSR.max) {
        color = VSR.color;
        break;
      }
    } else {
      if (VSR.min === null) {
        if (params.varianceScore < VSR.max) {
          color = VSR.color;
          break;
        }
      } else if (VSR.max === null) {
        if (params.varianceScore > VSR.min) {
          color = VSR.color;
          break;
        }
      } else {
        if (VSR.min === null) {
          if (params.varianceScore < VSR.max) {
            color = VSR.color;
            break;
          }
        } else if (VSR.max === null) {
          if (params.varianceScore > VSR.min) {
            color = VSR.color;
            break;
          }
        } else {
          if (
            params.varianceScore > VSR.min &&
            params.varianceScore < VSR.max
          ) {
            color = VSR.color;
            break;
          }
        }
      }
    }
  }
  return color;
};

const _getChartLineColors = (stats, gridType) => {
  const RANGES = _getVarianceScoreRangeDefaults(gridType);
  if (RANGES && Array.isArray(RANGES) && stats && stats.percentiles) {
    const PERCENTILES = stats.percentiles;
    if (_isZeroCasePercentiles(gridType, PERCENTILES)) {
      return _defaultLineColors(gridType, PERCENTILES, RANGES);
    }
    let lines = [];
    for (let i = 0; i < RANGES.length; i++) {
      let min = null;
      let max = null;
      if (RANGES[i].minLevel !== null) {
        min = PERCENTILES[RANGES[i].minLevel];
      }
      if (RANGES[i].maxLevel !== null) {
        max = PERCENTILES[RANGES[i].maxLevel];
      }
      lines.push({
        id: RANGES[i].id,
        color: RANGES[i].color,
        min: min,
        max: max,
      });
    }
    return lines;
  }
  return null;
};

const _isZeroCasePercentiles = (gridType, percentiles) => {
  if (gridType !== GRIDS_TYPES.activity) {
    return false;
  }
  return (
    percentiles.green === 0 &&
    percentiles.blue === 0 &&
    percentiles.yellow === 0
  );
};

const _defaultLineColors = (gridType, percentiles, ranges) => {
  if (gridType !== GRIDS_TYPES.activity) {
    return [];
  }
  const LOWER = {
    id: ranges[2].id,
    color: ranges[2].color,
    min: -0.8,
    max: 0.02,
  };
  const MIDDLE = {
    id: ranges[3].id,
    color: ranges[3].color,
    min: 0.02,
    max: (percentiles[ranges[3].maxLevel] || 1) - 0.02,
  };
  const UPPER = {
    id: ranges[4].id,
    color: ranges[4].color,
    min: (percentiles[ranges[3].maxLevel] || 1) - 0.02,
    max: null,
  };
  return [LOWER, MIDDLE, UPPER];
};

const _getLocationByType = async (params) => {
  let location;
  let result;
  if (params.type === GRIDS_STABILITY_TYPES.country) {
    result = await getCountryByCode(params.code);
    location = JSON.parse(result).data;
    if (lodashGet(location, "properties.displayName")) {
      location.properties.locationName = location.properties.displayName;
    }
  } else if (params.type === GRIDS_STABILITY_TYPES.region) {
    location = getGeohashByCode(params.code);
  } else if (params.type === GRIDS_STABILITY_TYPES.city) {
    result = await getCityByCode(params.code);
    location = JSON.parse(result).data;
    if (lodashGet(location, "properties.displayName")) {
      location.properties.locationName = `${location.properties.displayName}, ${location.properties.countryName}`;
    }
  }
  return location;
};

const _getCityObjectNearGeohash = async (geohash) => {
  let result;
  try {
    const AOI = Geohash.decode(geohash);
    const url =
      ENDPOINT_AOIS +
      "/closest-city?lat=" +
      AOI.lat.toFixed(2) +
      "&lon=" +
      AOI.lon.toFixed(2);
    result = await httpGet({ url: url });
    result = JSON.parse(result);
    return {
      distance: result.data.properties.distance,
      closest: result.data,
    };
  } catch (error) {
    console.error("Unable to retrieve city near geohash", error);
    return null;
  } finally {
    result = null;
  }
};

const _getCityNearGeohash = async (geohash, distance, includeCountry) => {
  let near_result = await _getCityObjectNearGeohash(geohash);
  if (near_result) {
    if (near_result.distance < distance) {
      let text = near_result.closest.properties.displayName;
      if (includeCountry) {
        text += `, ${near_result.closest.properties.countryName}`;
      }
      return text;
    } else {
      return near_result.closest.properties.countryName;
    }
  }
  return null;
};

const _getColorByStabilityScore = (score) => {
  let ranges = GRIDS_SCORE_RANGE_DEFAULTS.stability;
  for (let i = 0; i < ranges.length; i++) {
    let cur = ranges[i];
    if (score <= cur.max) {
      return cur.color;
    }
  }
};
